import { createClient } from '@supabase/supabase-js';
import { API_CONFIG } from '../utils/constants.js';

// Initialize Supabase client
const supabase = createClient(
  API_CONFIG.supabaseUrl,
  API_CONFIG.supabaseAnonKey
);

/**
 * Supabase service class for handling database operations
 */
export class SupabaseService {
  constructor() {
    this.client = supabase;
  }

  /**
   * Create a new whitelist entry
   * @param {Object} data - Entry data
   * @param {string} data.name - User's name
   * @param {string} data.email - User's email
   * @param {string} data.walletAddress - User's BNB wallet address
   * @param {string} [data.ipAddress] - User's IP address
   * @param {string} [data.userAgent] - User's browser user agent
   * @returns {Promise<Object>} Created entry
   */
  async createWhitelistEntry(data) {
    try {
      const entryData = {
        name: data.name.trim(),
        email: data.email.toLowerCase().trim(),
        wallet_address: data.walletAddress.trim(),
        ip_address: data.ipAddress || null,
        user_agent: data.userAgent || null,
        email_sent: false,
        email_verified: false
      };

      const { data: entry, error } = await this.client
        .from('whitelist_entries')
        .insert([entryData])
        .select()
        .single();

      if (error) {
        console.error('Supabase error creating entry:', error);
        
        // Handle specific error types
        if (error.code === '23505') { // Unique constraint violation
          if (error.message.includes('email')) {
            throw new Error('DUPLICATE_EMAIL');
          } else if (error.message.includes('wallet_address')) {
            throw new Error('DUPLICATE_WALLET');
          }
        }
        
        throw new Error('DATABASE_ERROR');
      }

      return this._transformEntry(entry);
    } catch (error) {
      console.error('Error creating whitelist entry:', error);
      
      if (error.message.includes('DUPLICATE_') || error.message === 'DATABASE_ERROR') {
        throw error;
      }
      
      throw new Error('DATABASE_ERROR');
    }
  }

  /**
   * Check if an entry already exists with the given email or wallet address
   * @param {string} email - Email to check
   * @param {string} walletAddress - Wallet address to check
   * @returns {Promise<Object>} Object with duplicate status
   */
  async checkDuplicateEntry(email, walletAddress) {
    try {
      const { data, error } = await this.client
        .from('whitelist_entries')
        .select('email, wallet_address')
        .or(`email.eq.${email.toLowerCase().trim()},wallet_address.eq.${walletAddress.trim()}`);

      if (error) {
        console.error('Error checking duplicates:', error);
        throw new Error('DATABASE_ERROR');
      }

      const emailExists = data.some(entry => entry.email === email.toLowerCase().trim());
      const walletExists = data.some(entry => entry.wallet_address === walletAddress.trim());

      return {
        isDuplicate: emailExists || walletExists,
        emailExists,
        walletExists
      };
    } catch (error) {
      console.error('Error checking duplicate entry:', error);
      throw new Error('DATABASE_ERROR');
    }
  }

  /**
   * Get total count of whitelist entries
   * @returns {Promise<number>} Total count
   */
  async getEntryCount() {
    try {
      const { count, error } = await this.client
        .from('whitelist_entries')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('Error getting entry count:', error);
        throw new Error('DATABASE_ERROR');
      }

      return count || 0;
    } catch (error) {
      console.error('Error getting entry count:', error);
      return 0;
    }
  }

  /**
   * Get whitelist entry by ID
   * @param {string} id - Entry ID
   * @returns {Promise<Object|null>} Entry or null if not found
   */
  async getEntryById(id) {
    try {
      const { data, error } = await this.client
        .from('whitelist_entries')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') { // No rows returned
          return null;
        }
        console.error('Error getting entry by ID:', error);
        throw new Error('DATABASE_ERROR');
      }

      return this._transformEntry(data);
    } catch (error) {
      console.error('Error getting entry by ID:', error);
      throw new Error('DATABASE_ERROR');
    }
  }

  /**
   * Update email sent status for an entry
   * @param {string} id - Entry ID
   * @param {boolean} emailSent - Email sent status
   * @returns {Promise<Object>} Updated entry
   */
  async updateEmailSentStatus(id, emailSent) {
    try {
      const { data, error } = await this.client
        .from('whitelist_entries')
        .update({ email_sent: emailSent })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating email sent status:', error);
        throw new Error('DATABASE_ERROR');
      }

      return this._transformEntry(data);
    } catch (error) {
      console.error('Error updating email sent status:', error);
      throw new Error('DATABASE_ERROR');
    }
  }

  /**
   * Log email sending attempt
   * @param {Object} logData - Email log data
   * @param {string} logData.whitelistEntryId - Related entry ID
   * @param {string} logData.emailType - Type of email ('user_confirmation' or 'admin_notification')
   * @param {string} logData.recipientEmail - Recipient email address
   * @param {string} logData.status - Email status ('pending', 'sent', 'failed')
   * @param {string} [logData.errorMessage] - Error message if failed
   * @param {string} [logData.emailProviderId] - Provider-specific ID
   * @returns {Promise<Object>} Created log entry
   */
  async logEmailAttempt(logData) {
    try {
      const emailLogData = {
        whitelist_entry_id: logData.whitelistEntryId,
        email_type: logData.emailType,
        recipient_email: logData.recipientEmail.toLowerCase().trim(),
        status: logData.status,
        error_message: logData.errorMessage || null,
        email_provider_id: logData.emailProviderId || null
      };

      const { data, error } = await this.client
        .from('email_logs')
        .insert([emailLogData])
        .select()
        .single();

      if (error) {
        console.error('Error logging email attempt:', error);
        throw new Error('DATABASE_ERROR');
      }

      return this._transformEmailLog(data);
    } catch (error) {
      console.error('Error logging email attempt:', error);
      throw new Error('DATABASE_ERROR');
    }
  }

  /**
   * Get email logs for an entry
   * @param {string} entryId - Whitelist entry ID
   * @returns {Promise<Array>} Array of email logs
   */
  async getEmailLogs(entryId) {
    try {
      const { data, error } = await this.client
        .from('email_logs')
        .select('*')
        .eq('whitelist_entry_id', entryId)
        .order('sent_at', { ascending: false });

      if (error) {
        console.error('Error getting email logs:', error);
        throw new Error('DATABASE_ERROR');
      }

      return data.map(log => this._transformEmailLog(log));
    } catch (error) {
      console.error('Error getting email logs:', error);
      return [];
    }
  }

  /**
   * Transform database entry to application format
   * @private
   */
  _transformEntry(entry) {
    if (!entry) return null;

    return {
      id: entry.id,
      name: entry.name,
      email: entry.email,
      walletAddress: entry.wallet_address,
      createdAt: entry.created_at,
      updatedAt: entry.updated_at,
      emailSent: entry.email_sent,
      emailVerified: entry.email_verified,
      verifiedAt: entry.verified_at,
      ipAddress: entry.ip_address,
      userAgent: entry.user_agent
    };
  }

  /**
   * Transform database email log to application format
   * @private
   */
  _transformEmailLog(log) {
    if (!log) return null;

    return {
      id: log.id,
      whitelistEntryId: log.whitelist_entry_id,
      emailType: log.email_type,
      recipientEmail: log.recipient_email,
      sentAt: log.sent_at,
      status: log.status,
      errorMessage: log.error_message,
      emailProviderId: log.email_provider_id
    };
  }

  /**
   * Test database connection
   * @returns {Promise<boolean>} Connection status
   */
  async testConnection() {
    try {
      const { data, error } = await this.client
        .from('whitelist_entries')
        .select('count', { count: 'exact', head: true });

      return !error;
    } catch (error) {
      console.error('Database connection test failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const supabaseService = new SupabaseService();

// Export client for direct access if needed
export { supabase };

// Export default
export default supabaseService;